import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// @mui
import {
  Card,
  Table,
  Stack,
  Popover,
  MenuItem,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
// components
import { deleteMultipleDataCaptures, searchDataCaptures, getCachedSessionsList, invalidateSessionsListCache } from '../services/dataCaptureService';
import { getCachedSharedDataCaptures, invalidateSharedDataCapturesCache } from '../services/shareService';
import useAuth from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
// sections
import { SessionTableHead, SessionListToolbar, SessionTableBody } from '../sections/@dashboard/sessions';

import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'description', label: 'Description', alignRight: false },
  { id: 'startTime', label: 'Start Time', alignRight: false },
  { id: 'lastModified', label: 'Last Modified', alignRight: true },
];

// ----------------------------------------------------------------------

export default function SessionListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [open, setOpen] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('lastModified');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sessions, setSessions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [sharedSessions, setSharedSessions] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getCachedSessionsList();
        setSessions(data);
        setLoading(false);
        // eslint-disable-next-line no-unused-vars
      } catch (_) {
        navigate('/');
      }
    }
    fetchData();
  }, [navigate]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      invalidateSessionsListCache();
      invalidateSharedDataCapturesCache();
      const data = await getCachedSessionsList();
      setSessions(data);
      await fetchSharedSessions();
    } finally {
      setRefreshing(false);
    }
  };

  const fetchSharedSessions = useCallback(async () => {
    try {
      setSharedLoading(true);
      const data = await getCachedSharedDataCaptures();
      setSharedSessions(data || []);
    } catch (error) {
      console.error('Failed to fetch shared sessions:', error);
      setSharedSessions([]);
    } finally {
      setSharedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSharedSessions();
  }, [fetchSharedSessions]);

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(displayedSessions.map((session) => session.id));
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleDelete = async () => {
    setConfirmDialogOpen(true);
    handleCloseMenu();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteMultipleDataCaptures(selected);
      setSelected([]);
      invalidateSessionsListCache();
      const data = await searchDataCaptures({ query: '' });
      setSessions(data);
      toast.success('Recordings deleted successfully');
    } catch (error) {
      toast.error('Failed to delete recordings');
    }
    setConfirmDialogOpen(false);
  };

  const getFilteredSessions = () => {
    switch (activeTab) {
      case 'mine':
        return sessions;
      case 'shared':
        return sharedSessions;
      case 'all':
      default: {
        const allSessions = [...sessions];
        sharedSessions.forEach((shared) => {
          if (!allSessions.find((s) => s.id === shared.id)) {
            allSessions.push(shared);
          }
        });
        return allSessions;
      }
    }
  };
  const tabSessions = getFilteredSessions();
  const displayedSessions = filterName
    ? tabSessions.filter((session) => session.name?.toLowerCase().includes(filterName.toLowerCase()))
    : tabSessions;
  const hasSharedSessions = sharedSessions.length > 0;

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - displayedSessions.length) : 0;

  if (loading) return <LoadingSpinner message="Loading recordings..." />;
  return (
    <>
      <Helmet>
        <title> VPSI | Recordings </title>
      </Helmet>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Recordings
          </Typography>
          <IconButton color="primary" onClick={handleRefresh} disabled={refreshing} title="Refresh Recordings">
            <Iconify icon="eva:refresh-fill" />
          </IconButton>
        </Stack>
        {hasSharedSessions && (
          <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 2 }}>
            <Tab label="All" value="all" />
            <Tab label="My Recordings" value="mine" />
            <Tab label="Shared with Me" value="shared" />
          </Tabs>
        )}
        <Card>
          <SessionListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onDelete={handleDelete}
          />
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <SessionTableHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={displayedSessions.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <SessionTableBody
                  sessions={displayedSessions}
                  handleClick={handleClick}
                  selected={selected}
                  order={order}
                  orderBy={orderBy}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  emptyRows={emptyRows}
                  user={user}
                />
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={displayedSessions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={handleDelete}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Popover>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selected.length} selected recording{selected.length !== 1 ? 's' : ''}? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer />
    </>
  );
}
